import dedent from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Provider,
  QueryAndOutputs,
  TableSchema,
  WorkflowCodeType,
  WorkflowComponentCheckInputValuesParams,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType,
  WorkflowComponentParameterMode,
  WorkflowComponentValue,
  WorkflowParameterValueErrorType
} from '../types'
import { isExpressionValue } from '../utils'
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent'

const RESERVED_COLUMNS = ['geoid', 'h3', 'quadbin']

class SpatialCompositeSupervisedSF extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.spatialcompositesupervised',
        group: 'Statistics',
        title: 'Composite Score Supervised',
        description: `
        This component derives a spatial composite score as the
        residuals of a regression model which is used to detect
        areas of under- and over-prediction.
        The response variable should be measurable and correlated
        with the set of variables defining the score.
        For each data point, the residual is defined as the observed
        value minus the predicted value. Rows with a NULL value if any
        of the input variables are dropped.
      `
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'indexcol',
          type: WorkflowComponentParamType.Column,
          parent: 'source',
          title: 'Column with unique geographic identifier',
          allowedColumns: RESERVED_COLUMNS,
          noDefault: true
        },
        {
          name: 'inputpredictors',
          title: 'Input variables',
          description: 'The columns containing the model variables.',
          type: WorkflowComponentParamType.Column,
          parent: 'source',
          mode: WorkflowComponentParameterMode.Multiple,
          noDefault: true
        },
        {
          name: 'modelvariablecolumn',
          type: WorkflowComponentParamType.Column,
          parent: 'source',
          title: 'Response variable',
          description: 'The column containing the response variable.',
          excludedColumns: RESERVED_COLUMNS,
          noDefault: true
        },
        {
          name: 'modelregressor',
          type: WorkflowComponentParamType.String,
          title: 'Model regressor',
          default: 'snowflake.ml.modeling.linear_model.LinearRegression',
          description: 'The model regressor type.'
        },
        {
          name: 'modeloptions',
          type: WorkflowComponentParamType.String,
          title: 'Model options',
          mode: WorkflowComponentParameterMode.Multiline,
          allowExpressions: false,
          optional: true,
          placeholder:
            '{"scaler": {"class": "snowflake.ml.modeling.preprocessing.MinMaxScaler","options": {"feature_range": [0, 1]}}}'
        },
        {
          name: 'bucketizemethod',
          type: WorkflowComponentParamType.Selection,
          title: 'Output formatting',
          options: ['NONE', 'EQUAL_INTERVALS_ZERO_CENTERED'],
          default: 'NONE'
        },
        {
          name: 'automaticnbuckets',
          type: WorkflowComponentParamType.Boolean,
          title: 'Define number of buckets automatically',
          default: false,
          showIf: [
            {
              parameter: 'bucketizemethod',
              value: 'EQUAL_INTERVALS_ZERO_CENTERED'
            }
          ]
        },
        {
          name: 'nbuckets',
          type: WorkflowComponentParamType.Number,
          title: 'Number of buckets',
          min: 2,
          max: 100,
          default: 5,
          showIf: [
            {
              parameter: 'automaticnbuckets',
              value: false
            },
            {
              parameter: 'bucketizemethod',
              value: 'EQUAL_INTERVALS_ZERO_CENTERED'
            }
          ]
        },
        {
          name: 'removeoutliers',
          type: WorkflowComponentParamType.Boolean,
          title: 'Remove outliers',
          default: true,
          showIf: [
            {
              parameter: 'bucketizemethod',
              value: 'EQUAL_INTERVALS_ZERO_CENTERED'
            }
          ]
        },
        {
          name: 'r2thr',
          type: WorkflowComponentParamType.Number,
          title: 'R-squared threshold',
          max: 1,
          default: 0.5
        }
      ]
    )
    this.supportedProviders = [Provider.Snowflake]
  }

  async getCode({
    workflow,
    params,
    connection,
    envVars,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    const preparedParams = this._prepareParameters(params)
    const dryRun = codeType === WorkflowCodeType.DryRun
    const tablename = connection.getOutputTableName(
      workflow.id,
      componentName,
      params,
      'result',
      codeType
    )
    const columns = preparedParams.inputpredictors as Array<string>

    let modelOptions = preparedParams.modeloptions
      ? JSON.parse(preparedParams.modeloptions as string)
      : {}
    modelOptions['input_label'] = preparedParams.modelvariablecolumn
    modelOptions['regressor'] = { class: preparedParams.modelregressor }
    const options = {
      model_options: modelOptions,
      bucketize_method:
        preparedParams.bucketizemethod === 'NONE'
          ? undefined
          : preparedParams.bucketizemethod,
      remove_outliers: preparedParams.removeoutliers ?? undefined,
      r2_thr:
        preparedParams.r2thr !== undefined
          ? parseFloat(preparedParams.r2thr as string)
          : undefined,
      nbuckets:
        preparedParams.nbuckets !== undefined
          ? parseInt(preparedParams.nbuckets as string)
          : undefined
    }

    let query = ''
    switch (connection.type) {
      case Provider.Snowflake: {
        if (dryRun) {
          const select = dedent`
                  SELECT ${
                    preparedParams.indexcol || '1 AS index'
                  }, 1.1 AS spatial_score
                    FROM ${connection.quoteName(
                      preparedParams.source as string
                    )}`
          query = connection.getCreateTempTableQuery(
            tablename,
            select,
            codeType,
            null
          )
        } else {
          query = dedent`DROP TABLE IF EXISTS ${connection.quoteName(
            tablename
          )};
                          CALL ${connection.quoteName(
                            envVars.analyticsToolboxDataset as string
                          )}.CREATE_SPATIAL_COMPOSITE_SUPERVISED(
                            'SELECT ${preparedParams.indexcol}, ${columns.join(
            ', '
          )} FROM ${connection.quoteName(preparedParams.source as string)}',
                              '${preparedParams.indexcol}',
                              '${tablename}',
                              $$
                              ${JSON.stringify(options, null, 2)}
                              $$);`
        }
        break
      }
      default:
        return null
    }
    return {
      outputs: { result: tablename },
      query
    }
  }

  checkInputValues({
    params,
    resolvedParamValues,
    connection,
    dryRun,
    schemas,
    variables
  }: WorkflowComponentCheckInputValuesParams) {
    const problems = super.checkInputValues({
      params,
      resolvedParamValues,
      connection,
      dryRun,
      schemas,
      variables
    })
    if (resolvedParamValues.inputpredictors) {
      const inputpredictors =
        resolvedParamValues.inputpredictors as Array<string>
      if (inputpredictors.length === 0) {
        problems.push({
          parameter: 'inputpredictors',
          type: WorkflowParameterValueErrorType.MissingValue,
          message: 'At least one input variable must be added'
        })
      }
      const indexcol = resolvedParamValues.indexcol as string
      if (inputpredictors.includes(indexcol)) {
        problems.push({
          parameter: 'inputpredictors',
          type: WorkflowParameterValueErrorType.WrongValue,
          message: 'Variable columns cannot contain the index column'
        })
      }
      if (resolvedParamValues.modelvariablecolumn) {
        const modelvariablecolumn =
          resolvedParamValues.modelvariablecolumn as string
        if (!inputpredictors.includes(modelvariablecolumn)) {
          problems.push({
            parameter: 'modelvariablecolumn',
            type: WorkflowParameterValueErrorType.WrongValue,
            message:
              'Model variable column must be contained in variable columns'
          })
        }
      }
    }
    if (
      resolvedParamValues.modeloptions &&
      !isExpressionValue(params.modeloptions as WorkflowComponentValue)
    ) {
      try {
        const json = JSON.parse(resolvedParamValues.modeloptions as string)
      } catch (e) {
        problems.push({
          parameter: 'modeloptions',
          type: WorkflowParameterValueErrorType.WrongValue,
          message: 'Invalid JSON string'
        })
      }
    }

    return problems
  }

  requiredAnalyticsToolboxVersion(provider: Provider): string | null {
    switch (provider) {
      case Provider.Snowflake:
        return '1.8.0'
      default:
        return '1.0.0'
    }
  }

  getVersion(provider: Provider): string {
    return '1.0.0'
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ columns, params }: GetOutputSchemaParams): TableSchema {
    const schema = [{ name: 'spatial_score', type: ColumnDataType.Number }]
    if (params.indexcol) {
      const indexCol = columns.source.find(
        (col) => col.name === params.indexcol
      )
      if (indexCol == undefined) {
        return []
      }
      schema.push(indexCol)
    } else {
      schema.push({ name: 'index', type: ColumnDataType.Number })
    }
    return schema
  }
}

export { SpatialCompositeSupervisedSF }
